
import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';

interface PhotoDelivery {
  id: string;
  file_name: string;
  file_url: string;
  file_size: number;
  upload_date: string;
  download_count: number;
}

interface Order {
  id: string;
  customer_name: string;
  customer_email: string;
  property_address: string;
  package_type: string;
  total_amount: number;
  delivery_status: string;
  delivery_notes: string;
  created_at: string;
  photos: PhotoDelivery[];
}

export default function AdminPanel() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploadingOrderId, setUploadingOrderId] = useState<string | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [deliveryNotes, setDeliveryNotes] = useState('');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const { data: ordersData, error } = await supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch photos for each order
      const ordersWithPhotos = await Promise.all(
        (ordersData || []).map(async (order) => {
          const { data: photosData } = await supabase
            .from('photo_deliveries')
            .select('*')
            .eq('order_id', order.id)
            .order('upload_date', { ascending: false });

          return {
            ...order,
            photos: photosData || []
          };
        })
      );

      setOrders(ordersWithPhotos);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (orderId: string) => {
    if (!selectedFiles || selectedFiles.length === 0) {
      alert('Please select files to upload');
      return;
    }

    setUploadingOrderId(orderId);

    try {
      const uploadPromises = Array.from(selectedFiles).map(async (file) => {
        // Upload to Supabase Storage
        const fileName = `${orderId}/${Date.now()}_${file.name}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('photo-deliveries')
          .upload(fileName, file);

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: urlData } = supabase.storage
          .from('photo-deliveries')
          .getPublicUrl(fileName);

        // Save to database
        const { error: dbError } = await supabase
          .from('photo_deliveries')
          .insert({
            order_id: orderId,
            file_name: file.name,
            file_url: urlData.publicUrl,
            file_size: file.size
          });

        if (dbError) throw dbError;
      });

      await Promise.all(uploadPromises);

      // Update order status
      await supabase
        .from('orders')
        .update({ 
          delivery_status: 'uploaded',
          delivery_notes: deliveryNotes 
        })
        .eq('id', orderId);

      alert('Photos uploaded successfully!');
      setSelectedFiles(null);
      setDeliveryNotes('');
      fetchOrders();
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed. Please try again.');
    } finally {
      setUploadingOrderId(null);
    }
  };

  const updateOrderStatus = async (orderId: string, status: string, notes: string) => {
    try {
      await supabase
        .from('orders')
        .update({ 
          delivery_status: status,
          delivery_notes: notes 
        })
        .eq('id', orderId);

      fetchOrders();
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Admin Panel</h1>
          <p className="text-gray-600 mt-2">Manage orders and upload photos for clients</p>
        </div>

        <div className="space-y-6">
          {orders.map((order) => (
            <div key={order.id} className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{order.property_address}</h3>
                    <p className="text-gray-600">Order #{order.id.substring(0, 8)} • {order.customer_name}</p>
                    <p className="text-gray-500 text-sm">{order.customer_email}</p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                      order.delivery_status === 'uploaded' 
                        ? 'bg-green-100 text-green-800'
                        : order.delivery_status === 'downloaded'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {order.delivery_status === 'uploaded' ? 'Photos Uploaded' : 
                       order.delivery_status === 'downloaded' ? 'Downloaded' : 'Pending'}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-gray-600">
                  <div>
                    <span className="font-medium">Package:</span> {order.package_type}
                  </div>
                  <div>
                    <span className="font-medium">Amount:</span> ${order.total_amount}
                  </div>
                  <div>
                    <span className="font-medium">Booked:</span> {formatDate(order.created_at)}
                  </div>
                  <div>
                    <span className="font-medium">Photos:</span> {order.photos.length} uploaded
                  </div>
                </div>
              </div>

              <div className="p-6">
                {order.photos.length > 0 ? (
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Uploaded Photos</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {order.photos.map((photo) => (
                        <div key={photo.id} className="flex items-center space-x-3 p-3 border border-gray-200 rounded-lg">
                          <div className="w-8 h-8 bg-teal-100 rounded flex items-center justify-center">
                            <i className="ri-image-line text-teal-600"></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{photo.file_name}</p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(photo.file_size)} • {photo.download_count} downloads
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Upload Photos</h4>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                      <div className="text-center">
                        <i className="ri-upload-cloud-line text-4xl text-gray-400 mb-4"></i>
                        <div className="mb-4">
                          <label htmlFor={`file-upload-${order.id}`} className="cursor-pointer">
                            <span className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors">
                              Select Photos
                            </span>
                            <input
                              id={`file-upload-${order.id}`}
                              type="file"
                              multiple
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => setSelectedFiles(e.target.files)}
                            />
                          </label>
                        </div>
                        {selectedFiles && selectedFiles.length > 0 && (
                          <div className="mb-4">
                            <p className="text-sm text-gray-600 mb-2">
                              {selectedFiles.length} files selected
                            </p>
                            <div className="mb-3">
                              <textarea
                                placeholder="Add delivery notes (optional)"
                                value={deliveryNotes}
                                onChange={(e) => setDeliveryNotes(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                rows={2}
                              />
                            </div>
                            <button
                              onClick={() => handleFileUpload(order.id)}
                              disabled={uploadingOrderId === order.id}
                              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                            >
                              {uploadingOrderId === order.id ? 'Uploading...' : 'Upload Photos'}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {order.delivery_notes && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <span className="font-medium">Delivery Notes:</span> {order.delivery_notes}
                    </p>
                  </div>
                )}

                <div className="flex space-x-3">
                  <button
                    onClick={() => updateOrderStatus(order.id, 'pending', '')}
                    className="px-3 py-1 text-sm bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 transition-colors"
                  >
                    Mark as Pending
                  </button>
                  <button
                    onClick={() => updateOrderStatus(order.id, 'uploaded', order.delivery_notes)}
                    className="px-3 py-1 text-sm bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition-colors"
                  >
                    Mark as Uploaded
                  </button>
                  <button
                    onClick={() => updateOrderStatus(order.id, 'downloaded', order.delivery_notes)}
                    className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors"
                  >
                    Mark as Downloaded
                  </button>
                </div>
              </div>
            </div>
          ))}

          {orders.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-list-line text-gray-400 text-2xl"></i>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Orders Yet</h3>
              <p className="text-gray-600">Orders will appear here once clients start booking.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
